import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Download,
  FileText,
  Loader2,
  LogOut,
  Play,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal, Overline } from "@/components/Reveal";
import { WellnessTimeline } from "@/components/journey/WellnessTimeline";
import { ProgressChart } from "@/components/journey/ProgressChart";
import { ComparePanel } from "@/components/journey/ComparePanel";
import { JourneyGlance } from "@/components/journey/JourneyGlance";
import { IntentionsPanel } from "@/components/journey/IntentionsPanel";
import { SITE } from "@/config/site";
import { track } from "@/lib/analytics";
import { useBookingUrl } from "@/context/ContentContext";

const LABEL_STYLE = {
  Thriving: "bg-gold/15 text-gold-dark",
  "Finding Balance": "bg-sage text-forest",
  "Out of Balance": "bg-[#F1E9D8] text-[#7A6A45]",
  "Survival Mode": "bg-forest text-ivory",
  "Insufficient Information": "bg-charcoal/5 text-taupe",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function MyWellnessJourney() {
  const { member, setMember, logout } = useAuth();
  const bookingUrl = useBookingUrl();
  const [entries, setEntries] = useState(null);
  const [compare, setCompare] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    document.title = "My Wellness Journey | Rooted Healing";
    api
      .get("/assessments/timeline")
      .then(({ data }) => setEntries(data.entries))
      .catch(() => setEntries([]));
  }, []);

  const toggleCompare = (id) => {
    setCompareSelection((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id].slice(-2);
    });
  };

  const runCompare = async () => {
    if (compareSelection.length !== 2) return;
    const sorted = [...compareSelection].sort((a, b) => {
      const ea = entries.find((e) => e.assessment_id === a);
      const eb = entries.find((e) => e.assessment_id === b);
      return new Date(ea.created_at) - new Date(eb.created_at);
    });
    setComparing(true);
    try {
      const { data } = await api.get(
        `/assessments/compare?older=${sorted[0]}&newer=${sorted[1]}`
      );
      setCompare(data);
      track("assessment_compared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not compare right now."));
    }
    setComparing(false);
  };

  const downloadPdf = async (id) => {
    setDownloading(id);
    try {
      const res = await api.get(`/assessments/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rooted-wellness-profile.pdf";
      a.click();
      URL.revokeObjectURL(url);
      track("report_downloaded");
    } catch {
      toast.error("Could not download the report just now.");
    }
    setDownloading(null);
  };

  const togglePref = async (key, value) => {
    try {
      const { data } = await api.patch("/auth/preferences", { [key]: value });
      setMember(data);
      toast.success("Preference saved.");
    } catch {
      toast.error("Could not save that preference.");
    }
  };

  const latest = entries && entries.length > 0 ? entries[entries.length - 1] : null;
  const firstName = member?.name?.split(" ")[0] || "friend";
  const greeting =
    entries === null
      ? null
      : entries.length === 0
        ? "Your Wellness Journey Begins Here"
        : entries.length === 1
          ? "Your Starting Point Is Saved"
          : "See How Your Journey Has Evolved";

  return (
    <main data-testid="my-wellness-journey-page" className="pt-[76px]">
      <div className="border-b border-gold/30 bg-sage/70 px-5 py-3.5 text-center sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
          Results are provisional · pending Staci's final approval of the scoring
        </p>
      </div>

      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline>My Wellness Journey</Overline>
              <h1 className="font-serif text-3xl leading-tight text-forest sm:text-4xl lg:text-5xl">
                Welcome back, <em className="italic text-gold">{firstName}</em>
              </h1>
              {greeting && (
                <p
                  data-testid="dashboard-greeting"
                  className="mt-3 max-w-xl text-sm leading-relaxed text-charcoal/70"
                >
                  <span className="font-semibold text-forest">{greeting}.</span>{" "}
                  {entries.length === 0 &&
                    "Whenever you are ready, your first discovery is waiting below."}
                  {entries.length === 1 &&
                    `Your discovery from ${fmtDate(latest.created_at)} is saved. There is no schedule to keep. Return whenever life feels different.`}
                  {entries.length >= 2 &&
                    `Your most recent discovery was ${fmtDate(latest.created_at)}. Scroll down to explore how your answers have shifted over time.`}
                </p>
              )}
              {latest && (
                <p className="mt-3 flex items-center gap-2 text-sm text-taupe">
                  <CalendarDays className="h-4 w-4" />
                  Last discovery completed {fmtDate(latest.created_at)}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/assessment"
                data-testid="dashboard-start-assessment"
                onClick={() => track("assessment_start_click", { location: "dashboard" })}
                className="inline-flex items-center gap-2.5 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)]"
              >
                {latest ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {latest ? "Revisit My Wellness Discovery" : "Start Your Discovery"}
              </Link>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dashboard-book-consultation"
                onClick={() => track("booking_click", { location: "dashboard" })}
                className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-forest-deep transition-all duration-300 hover:bg-gold-soft"
              >
                Book a Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="dashboard-logout"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-full border border-forest/20 px-5 py-3.5 text-sm font-semibold text-forest transition-colors hover:border-gold hover:text-gold-dark"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </Reveal>

        {entries === null ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : entries.length === 0 ? (
          <Reveal delay={0.1}>
            <div className="mt-14 rounded-[2.5rem] border border-forest/10 bg-white/75 p-10 text-center shadow-[0_20px_50px_rgba(23,63,53,0.1)] sm:p-16">
              <Overline>Begin Here</Overline>
              <h2 className="mx-auto max-w-lg font-serif text-3xl leading-snug text-forest">
                Your first discovery is waiting
              </h2>
              <p className="mx-auto mt-4 max-w-md leading-relaxed text-charcoal/70">
                Thirty-six gentle questions, one calm sitting or several small ones. Your
                progress saves automatically and your answers stay private.
              </p>
              <Link
                to="/assessment"
                data-testid="dashboard-empty-start"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep"
              >
                Begin My Wellness Discovery
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal delay={0.08}>
              <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
                <h2 className="font-serif text-2xl text-forest sm:text-3xl">My Wellness Timeline</h2>
                {entries.length >= 2 && (
                  <button
                    data-testid="timeline-run-compare"
                    onClick={runCompare}
                    disabled={compareSelection.length !== 2 || comparing}
                    className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-40"
                  >
                    {comparing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Compare selected ({compareSelection.length}/2)
                  </button>
                )}
              </div>
              {entries.length >= 2 && (
                <p className="mt-2 text-sm text-taupe">
                  Select any two discoveries to see how your self-reported answers have shifted.
                </p>
              )}
            </Reveal>

            {compare && (
              <Reveal delay={0.05}>
                <div className="mt-8">
                  <ComparePanel data={compare} onClose={() => setCompare(null)} />
                </div>
              </Reveal>
            )}

            {entries.length >= 2 && (
              <Reveal delay={0.08}>
                <div className="mt-8">
                  <ProgressChart entries={entries} />
                </div>
              </Reveal>
            )}

            {entries.length >= 2 && (
              <Reveal delay={0.1}>
                <div className="mt-8">
                  <JourneyGlance entries={entries} />
                </div>
              </Reveal>
            )}

            {entries.length === 1 && (
              <Reveal delay={0.08}>
                <div
                  data-testid="journey-starting-point"
                  className="mt-8 rounded-[2rem] border border-forest/10 bg-white/75 p-8 shadow-[0_12px_30px_rgba(23,63,53,0.07)]"
                >
                  <h3 className="font-serif text-xl text-forest">Your Starting Point Is Saved</h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-charcoal/70">
                    This first discovery is the beginning of your personal timeline. When you
                    complete a future discovery, this space will grow into a chart and a
                    gentle summary of how your answers have shifted. There is no schedule to
                    keep. Return whenever life feels different.
                  </p>
                </div>
              </Reveal>
            )}

            <Reveal delay={0.12}>
              <div
                data-testid="dashboard-consultation-invite"
                className="mt-10 rounded-[2.5rem] border border-gold/25 bg-gradient-to-br from-sage/60 to-white/70 p-8 shadow-[0_16px_40px_rgba(23,63,53,0.08)] sm:p-10"
              >
                <Overline>A Gentle Next Step</Overline>
                <h2 className="mt-3 font-serif text-2xl leading-snug text-forest sm:text-3xl">
                  Sometimes, the next step is a conversation.
                </h2>
                <p className="mt-3 max-w-2xl leading-relaxed text-charcoal/70">
                  Your Wellness Discovery highlights patterns worth exploring. A personal
                  consultation with Staci can help you reflect on what you've discovered and
                  consider meaningful next steps.
                </p>
                <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    {
                      key: "terrain",
                      name: "Terrain & Truth Consultation",
                      minutes: "60 minutes",
                      price: "$88",
                      cta: "Book Terrain & Truth",
                    },
                    {
                      key: "full",
                      name: "Full Holistic Consultation",
                      minutes: "90 minutes · with iridology",
                      price: "$234",
                      cta: "Book Full Holistic Consultation",
                    },
                  ].map((o) => (
                    <div
                      key={o.key}
                      data-testid={`dashboard-consult-${o.key}`}
                      className="flex flex-col rounded-[1.75rem] border border-forest/12 bg-white/75 p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(23,63,53,0.12)]"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
                        {o.minutes}
                      </p>
                      <h3 className="mt-2 font-serif text-lg text-forest">{o.name}</h3>
                      <p className="mt-1 font-serif text-2xl text-forest">{o.price}</p>
                      <a
                        href={bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`dashboard-book-${o.key}`}
                        onClick={() => track("booking_click", { location: `dashboard-invite-${o.key}` })}
                        className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep"
                      >
                        {o.cta}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-xs leading-relaxed text-taupe">
                  Entirely optional, whenever it feels right. Iridology is a traditional
                  interpretive practice, not a medical diagnostic test.
                </p>
              </div>
            </Reveal>

            <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <WellnessTimeline
                  entries={entries}
                  selected={compareSelection}
                  onToggleCompare={toggleCompare}
                  onDownloadPdf={downloadPdf}
                  downloadingId={downloading}
                />
              </div>
              <div className="flex flex-col gap-8 lg:col-span-5">
                <Reveal delay={0.1}>
                  <div className="rounded-[2rem] border border-forest/10 bg-white/75 p-8 shadow-[0_12px_30px_rgba(23,63,53,0.07)]">
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-xl text-forest">Most Recent Profile</h3>
                      <span className="text-xs uppercase tracking-[0.18em] text-taupe">
                        {fmtDate(latest.created_at)}
                      </span>
                    </div>
                    <p
                      data-testid="dashboard-latest-overall"
                      className={`mt-5 inline-flex rounded-full px-6 py-2.5 font-serif text-2xl ${LABEL_STYLE[latest.overall] || LABEL_STYLE["Insufficient Information"]}`}
                    >
                      {latest.overall}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link
                        to={`/results/${latest.assessment_id}`}
                        data-testid="dashboard-view-profile"
                        className="group inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep"
                      >
                        View My Full Profile
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                      <button
                        data-testid="dashboard-download-pdf"
                        onClick={() => downloadPdf(latest.assessment_id)}
                        disabled={downloading === latest.assessment_id}
                        className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest transition-colors hover:border-gold hover:text-gold-dark disabled:opacity-50"
                      >
                        {downloading === latest.assessment_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Report PDF
                      </button>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={0.14}>
                  <IntentionsPanel entries={entries} />
                </Reveal>
              </div>
            </div>
          </>
        )}

        <Reveal delay={0.15}>
          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-forest/10 bg-white/75 p-8 shadow-[0_12px_30px_rgba(23,63,53,0.07)]">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-forest" />
                <h2 className="font-serif text-xl text-forest">My Reports</h2>
              </div>
              {entries && entries.length > 0 ? (
                <div className="mt-5 flex flex-col gap-3">
                  {[...entries].reverse().map((a) => (
                    <div
                      key={a.assessment_id}
                      className="flex items-center justify-between rounded-2xl border border-forest/10 px-5 py-4"
                    >
                      <div>
                        <p className="text-sm font-semibold text-forest">Extended Wellness Report</p>
                        <p className="text-xs text-taupe">{fmtDate(a.created_at)}</p>
                      </div>
                      <button
                        data-testid={`report-download-${a.assessment_id}`}
                        onClick={() => downloadPdf(a.assessment_id)}
                        disabled={downloading === a.assessment_id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-dark underline-offset-4 hover:underline disabled:opacity-50"
                      >
                        {downloading === a.assessment_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        PDF
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-5 text-sm leading-relaxed text-charcoal/65">
                  Your extended private reports will appear here after your first discovery.
                </p>
              )}
            </div>

            <div className="rounded-[2rem] border border-forest/10 bg-white/75 p-8 shadow-[0_12px_30px_rgba(23,63,53,0.07)]">
              <h2 className="font-serif text-xl text-forest">Account & Preferences</h2>
              <p className="mt-4 text-sm text-charcoal/70">
                <span className="font-semibold text-forest">{member?.name}</span>
                <br />
                {member?.email}
              </p>
              {["owner", "admin", "support", "marketing"].includes(member?.role) && (
                <Link
                  to="/admin"
                  data-testid="dashboard-admin-center-button"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-forest/25 bg-sage/60 px-6 py-3 text-sm font-semibold text-forest transition-all duration-300 hover:border-forest hover:bg-sage"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Open Admin Center
                </Link>
              )}
              <div className="mt-6 flex flex-col gap-4">
                <label className="flex cursor-pointer items-start gap-3 text-sm text-charcoal/75">
                  <input
                    type="checkbox"
                    data-testid="pref-newsletter-toggle"
                    checked={Boolean(member?.consents?.newsletter)}
                    onChange={(e) => togglePref("consent_newsletter", e.target.checked)}
                    className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  Send me the Rooted Healing newsletter
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-charcoal/75">
                  <input
                    type="checkbox"
                    data-testid="pref-reminders-toggle"
                    checked={Boolean(member?.consents?.reminders)}
                    onChange={(e) => togglePref("consent_reminders", e.target.checked)}
                    className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  Send me gentle reminders at about 90 and 180 days after a discovery
                </label>
              </div>
              <p className="mt-5 text-xs leading-relaxed text-taupe">
                Preferences are separate from your account and can be changed anytime.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
